/**
 * Cấu hình → "Thông tin tiệm" (chỉ chủ tiệm — docs/plans/thong-tin-tiem.md): sửa tên tiệm và ngành đã chọn lúc mở tiệm.
 * Ngành quyết định màu + biểu tượng của web quầy và app khách (danh mục `@sapa/shared`, ADR-082, ADR-115). Một nút Lưu cho cả thẻ.
 * Giá trị đầu đọc từ `toi` (máy chủ đã trả trong `/v1/auth/toi`); lưu xong gọi `onDaLuu` để tải lại `toi`.
 *
 * v0.23.0: mỗi chip ngành có chấm màu; dưới hàng chip có khung xem trước (không đổi màu trang tới khi lưu); đổi NGÀNH phải
 * qua hộp xác nhận "Đổi ngành" (đổi chỉ tên thì lưu thẳng) — hộp nói thêm tên gọi người làm / ngày nhắc quay lại khi khác (Q3).
 */
import { laNganhTiem, NGANH, type NganhTiem, nganhDeChon, TEN_NGANH } from '@sapa/shared'
import { useEffect, useRef, useState } from 'react'
import { duongDanAnhHopLe } from './anh-cua-hang.ts'
import { type ToiLaAi, thongTinTiemApi } from './api.ts'
import { TT } from './text/thong-tin-tiem.ts'
import { cauHoiDoiNganh } from './thong-tin-tiem-xac-nhan.ts'
import { XemTruocNganh } from './XemTruocNganh.tsx'

export function TheThongTinTiem({
  toi,
  onLoi,
  onXong,
  onDaLuu,
}: {
  toi: ToiLaAi
  onLoi: (e: unknown) => void
  onXong: (cau: string) => void
  onDaLuu: () => void
}) {
  const nganhDau: NganhTiem = laNganhTiem(toi.nganh) ? toi.nganh : 'khac'
  const [ten, setTen] = useState(toi.tenTiem)
  const [nganh, setNganh] = useState<NganhTiem>(nganhDau)
  const [dang, setDang] = useState(false)
  const [hoi, setHoi] = useState(false)
  const nutDoiRef = useRef<HTMLButtonElement>(null)
  const luuRef = useRef<HTMLButtonElement>(null)
  // Cùng thứ tự với form mở tiệm; ngành đang ẩn (vd `xe`) vẫn hiện nếu tiệm đang dùng.
  const ds = nganhDeChon(nganhDau)
  const tenGon = ten.trim()
  const anh = toi.anhCuaHang
  const doi = tenGon !== toi.tenTiem || nganh !== nganhDau
  const cau = cauHoiDoiNganh(nganhDau, nganh)

  async function luu() {
    setDang(true)
    try {
      await thongTinTiemApi.luu({ ten: tenGon, nganh })
      setHoi(false)
      onXong(TT.daLuu)
      onDaLuu()
    } catch (e) {
      onLoi(e)
    } finally {
      setDang(false)
    }
  }

  /** "Giữ ngành cũ": đóng hộp, chip về ngành đang dùng, KHÔNG gọi máy chủ (tên đang gõ vẫn giữ). */
  function giuNganhCu() {
    setHoi(false)
    setNganh(nganhDau)
  }

  // Hộp mở → focus vào "Đổi ngành"; Esc = "Giữ ngành cũ" (khuôn hộp hỏi lại ở HoSoBuoi.tsx).
  // biome-ignore lint/correctness/useExhaustiveDependencies: `giuNganhCu` chỉ dùng setState + `nganhDau`; chạy lại theo `hoi` là đủ.
  useEffect(() => {
    if (!hoi) return
    nutDoiRef.current?.focus()
    const khiBam = (e: KeyboardEvent) => {
      if (e.key === 'Escape') giuNganhCu()
    }
    document.addEventListener('keydown', khiBam)
    return () => {
      document.removeEventListener('keydown', khiBam)
      // Hộp đóng → trả focus về nút Lưu (nút vừa được vẽ lại), không để focus rơi về đầu trang.
      requestAnimationFrame(() => luuRef.current?.focus())
    }
  }, [hoi])

  return (
    <section className="the the-dem the-khoi">
      <h2>{TT.tieuDe}</h2>
      <p className="chu-phu">{TT.goiY}</p>
      <div className="truong">
        <label className="nhan" htmlFor="tt-ten-tiem">
          {TT.tenTiem}
        </label>
        <input
          id="tt-ten-tiem"
          className="o-nhap"
          value={ten}
          maxLength={120}
          onChange={(e) => setTen(e.target.value)}
        />
        {tenGon.length < 2 && <p className="goi-y-o">{TT.tenNgan}</p>}
      </div>
      <div className="truong">
        <span className="nhan">{TT.nganh}</span>
        <div className="hang-chip luoi-chip-2 luoi-chip-tt">
          {ds.map((n) => (
            <button
              key={n}
              type="button"
              className={`chip ${nganh === n ? 'dang-chon' : ''}`}
              aria-pressed={nganh === n}
              disabled={dang || hoi}
              onClick={() => setNganh(n)}
            >
              <span
                className="cham-mau-nganh"
                aria-hidden="true"
                style={{ backgroundColor: NGANH[n].mau.primary }}
              />
              {TEN_NGANH[n]}
            </button>
          ))}
        </div>
        <p className="goi-y-o">{TT.nganhGoiY}</p>
        {anh?.bat === true ? (
          // v0.23.3: tính năng ảnh đang mở cho tiệm này → khung cho thấy ảnh bìa / logo đang dùng (hoặc chữ cái đầu tên đang gõ).
          <XemTruocNganh
            nganh={nganh}
            anhBia={duongDanAnhHopLe(anh.bia)}
            logo={duongDanAnhHopLe(anh.logo)}
            ten={tenGon}
          />
        ) : (
          <XemTruocNganh nganh={nganh} />
        )}
      </div>
      {hoi && (
        <div className="hoi-doi-nganh" role="alertdialog" aria-labelledby="tt-hoi-tieu-de">
          <b id="tt-hoi-tieu-de">{TT.hoiDoiTieuDe}</b>
          <p>{TT.hoiDoiCau}</p>
          {cau.tenGoi !== undefined && (
            <p className="chu-phu">{TT.hoiDoiTenGoi(cau.tenGoi.cu, cau.tenGoi.moi)}</p>
          )}
          {cau.quayLai !== undefined && (
            <p className="chu-phu">{TT.hoiDoiQuayLai(cau.quayLai[0], cau.quayLai[1])}</p>
          )}
          <div className="hang-phu">
            <button
              ref={nutDoiRef}
              type="button"
              className="nut nut-chinh"
              disabled={dang}
              onClick={() => void luu()}
            >
              {dang ? TT.dangLuu : TT.xacNhanDoi}
            </button>
            <button type="button" className="nut nut-nhat" disabled={dang} onClick={giuNganhCu}>
              {TT.giuNganhCu}
            </button>
          </div>
        </div>
      )}
      {!hoi && (
        <div className="hang-nut-cuoi">
          <button
            ref={luuRef}
            type="button"
            className="nut nut-chinh nut-lon"
            disabled={dang || !doi || tenGon.length < 2}
            onClick={() => (nganh !== nganhDau ? setHoi(true) : void luu())}
          >
            {dang ? TT.dangLuu : TT.luu}
          </button>
        </div>
      )}
    </section>
  )
}
